import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { GitBranch, Plus, Power, QrCode, Download, Printer, RefreshCw, Loader2, Trash2 } from "lucide-react";

export default function Branches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canDelete = ["SUPER_ADMIN", "COMPANY_ADMIN"].includes(user.role);

  const load = () => api.get("/branches").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    if (user.role === "SUPER_ADMIN") api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  const openNew = () => { setEditing(null); setForm({ status: "active", working_hours: "09:00-20:00", timezone: "Asia/Kolkata", visitor_method: "email" }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success("Branch updated"); setOpen(false); load();
      } else {
        const { data } = await api.post("/branches", form);
        toast.success("Branch created — here's its visitor QR to print & place");
        setOpen(false); load();
        showQr(data);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (b) => { try { await api.patch(`/branches/${b.id}/status`); load(); } catch (e) { toast.error(apiError(e)); } };
  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/branches/${delTarget.id}`);
      toast.success("Branch deleted"); setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const showQr = async (b) => {
    try { const { data } = await api.get(`/branches/${b.id}/qr`); setQr({ ...data, name: b.name, kind: "visitor" }); } catch (e) { toast.error(apiError(e)); }
  };
  const showBookingQr = async (b) => {
    try { const { data } = await api.get(`/branches/${b.id}/booking-qr`); setQr({ ...data, name: b.name, kind: "booking" }); } catch (e) { toast.error(apiError(e)); }
  };

  const qrLabel = qr?.kind === "booking" ? "Meeting Room Booking QR" : "Visitor QR";
  const printQr = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const doc = w.document;
    doc.title = `${qr.name} QR`;
    const wrap = doc.createElement("div");
    wrap.style.cssText = "text-align:center;font-family:sans-serif;padding:40px";
    const h = doc.createElement("h2"); h.textContent = `${qr.name} — ${qrLabel}`;
    const img = doc.createElement("img"); img.src = qr.qr; img.style.width = "320px";
    const p = doc.createElement("p"); p.textContent = qr.url;
    wrap.append(h, img, p);
    doc.body.append(wrap);
    img.onload = () => w.print();
  };

  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Branches" subtitle="Each branch has a unique visitor QR and its own rooms & directory."
        actions={<Button onClick={openNew} data-testid="add-branch-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> New Branch</Button>} />

      {list.length === 0 ? (
        <EmptyState icon={GitBranch} title="No branches yet" subtitle="Create a branch to generate its visitor QR." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-5 sf-fade-up" data-testid={`branch-card-${b.code}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><GitBranch size={20} /></div>
                <StatusBadge status={b.status} />
              </div>
              <h3 className="font-display font-bold text-navy-900 mt-3 text-lg">{b.name}</h3>
              <p className="text-xs text-slate-400 font-mono">{b.code} · {b.company_name}</p>
              <p className="text-sm text-slate-500 mt-2">{b.address || "—"}</p>
              <p className="text-xs text-slate-400 mt-1">Hours {b.working_hours}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" className="bg-navy-900 hover:bg-navy-800" onClick={() => navigate(`/branches/${b.id}`)} data-testid={`view-branch-${b.code}`}>View</Button>
                <Button size="sm" variant="outline" onClick={() => showQr(b)} data-testid={`qr-branch-${b.code}`}><QrCode size={15} className="mr-1" /> Visitor QR</Button>
                <Button size="sm" variant="outline" onClick={() => showBookingQr(b)} data-testid={`booking-qr-branch-${b.code}`}><QrCode size={15} className="mr-1" /> Booking QR</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.code}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => toggle(b)}><Power size={15} /></Button>
                {canDelete && <Button size="sm" variant="outline" onClick={() => setDelTarget(b)} data-testid={`delete-branch-${b.code}`} className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Trash2 size={15} /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Branch" : "New Branch"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {user.role === "SUPER_ADMIN" && !editing && (
              <div><Label>Company *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="branch-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {[["name", "Branch Name *"], ["code", "Branch Code *"], ["address", "Address"], ["contact_number", "Contact Number"], ["email", "Email"], ["working_hours", "Working Hours"], ["timezone", "Timezone"]].map(([k, l]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`branch-${k}`} className="mt-1 h-10" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div className="sm:col-span-2">
              <Label>Visitor Identification Method *</Label>
              <Select value={form.visitor_method || "email"} onValueChange={(v) => setForm({ ...form, visitor_method: v })}>
                <SelectTrigger className="mt-1" data-testid="branch-visitor-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Method 01 — Host Employee Email</SelectItem>
                  <SelectItem value="cabin_code">Method 02 — Company Cabin Code</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">{(form.visitor_method === "cabin_code") ? "Visitor enters the company's cabin code to identify the host." : "Visitor enters the host employee's registered email."}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-branch-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={(v) => !v && setQr(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{qrLabel} · {qr?.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center">
            <img src={qr?.qr} alt="QR" className="w-56 h-56" data-testid="branch-qr-image" />
            <p className="text-xs text-slate-400 mt-2 break-all text-center">{qr?.url}</p>
            <div className="flex gap-2 mt-4">
              <a href={qr?.qr} download={`${qr?.name}-qr.png`}><Button variant="outline" data-testid="download-qr-btn"><Download size={15} className="mr-1" /> Download</Button></a>
              <Button variant="outline" onClick={printQr} data-testid="print-qr-btn"><Printer size={15} className="mr-1" /> Print</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-branch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the branch and <strong>everything under it</strong> — its rooms, all bookings, visitor records, notifications, settings, and its branch admins & receptionists. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-branch">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-branch" className="bg-rose-600 hover:bg-rose-700">
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Delete Branch"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
